import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import { Form, Modal, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import {
  createCampus,
  deleteCampus,
  getCampuses,
  updateCampus,
} from "../../services/campusService";
import { campusSchema } from "../../validators/campusValidators";
import { getInstitutions } from "../../services/institutionService";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const Campus = () => {
  const [campuses, setCampuses] = useState([]);
  const [filteredCampuses, setFilteredCampuses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [campusToEdit, setCampusToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [campusToDelete, setCampusToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");

  const { user } = useStateContext();

  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadCampuses();
    loadInstitutions();
  }, []);

  useEffect(() => {
    let filtered = campuses;

    if (searchTerm.trim() !== "") {
      filtered = campuses.filter((campus) =>
        `${campus.location} ${campus?.institution?.name || ""}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    setFilteredCampuses(filtered);
  }, [searchTerm, campuses, selectedInstitution]);

  const loadCampuses = async () => {
    setLoading(true);
    try {
      const { data } = await getCampuses();
      setCampuses(data.data);
      setFilteredCampuses(data.data);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Failed to load campuses");
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (campusToEdit) {
      onUpdateCampus(payload, resetForm);
    } else {
      onCreateCampus(payload, resetForm);
    }
  };

  const loadInstitutions = async () => {
    setLoading(true);
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      console.log(error);

      if (error) {
        toast.error(
          error?.response?.data?.error || "Failed to load Institutions"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const onCreateCampus = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createCampus(payload);

      setCampuses((prev) => [...prev, data.data]);

      toast.success("Campus created successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Campus was not created, try again!"
        );
      }
    }
  };

  const handleEdit = (campus) => {
    setCampusToEdit(campus);
    setIsModalOpen(true);
  };

  const onUpdateCampus = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalCampuses = [...campuses];

      const campusIndex = originalCampuses.findIndex(
        (campus) => campus.id === campusToEdit.id
      );

      if (campusIndex === -1) throw new Error("Campus not found.");

      const { data } = await updateCampus(campusToEdit.id, payload);

      originalCampuses[campusIndex] = data.data;

      setCampuses(originalCampuses);

      setCampusToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Campus updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(response?.data?.error || "Campus not updated, try again!");
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.location?.toUpperCase()}`
    );
    setCampusToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteCampus = async () => {
    const originalCampuses = [...campuses];

    const newCampuses = originalCampuses.filter(
      (campus) => campus.id !== campusToDelete.id
    );

    setCampuses(newCampuses);

    try {
      await deleteCampus(campusToDelete?.id);

      toast.success("Campus deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Campus not deleted, try again later"
        }`
      );

      setCampuses(originalCampuses);
    } finally {
      setIsDeleteModalOpen(false);

      setCampusToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setCampusToEdit(null);
    setCampusToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 300,
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
    },
    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Campus">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Campus">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading
        title="Campuses"
        subtitle1="Institution"
        subtitle2="Campuses"
      />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Campus List"
            count={filteredCampuses.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Campus"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredCampuses.map((item) => ({
              ...item,
              key: item.id,
            }))}
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {campusToEdit ? "Edit Campus" : "Add Campus"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                location: campusToEdit ? campusToEdit?.location : "",
                institution_id: campusToEdit
                  ? campusToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
              }}
              enableReinitialize
              validationSchema={campusSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                submitForm,
                setFieldValue,
                touched,
              }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    <TextInput
                      name="location"
                      title="Name"
                      placeholder="Enter Campus Location"
                      requiredField={true}
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                      autoFocus
                    />

                    {isInstAdmin ? (
                      <input
                        type="hidden"
                        name="institution_id"
                        value={
                          campusToEdit
                            ? campusToEdit?.institution?.id
                            : user?.institution?.id
                        }
                      />
                    ) : (
                      <CustomDropDownInput
                        name="institution_id"
                        title="Institution"
                        placeholder="Select an institution"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={institutions}
                        setFieldValue={setFieldValue}
                      />
                    )}

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteCampus}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default Campus;
